import {numberUtilsMixin} from "./numberUtilsMixin";
import {generateXAxisData} from "@/js/Tools";

const EventComparisonMixin = {
  mixins: [numberUtilsMixin],
  data() {
    return {
      // 样本数据配置
      sampleConfig: {
        color: '#ff4d4f',
        lineWidth: 1.5,
        zIndex: 1,
        animation: false,
        showSymbol: false,
        showAllSymbol: false
      },
      // 原始数据配置
      originalConfig: {
        zIndex: 2
      },
      // 图例配置
      legendConfig: {
        type: 'scroll',
        left: 50,
        bottom: 6,
        itemWidth: 12,
        itemHeight: 6,
        itemGap: 24,
        icon: 'rect'
      },
      // 默认值
      defaultValues: {
        waveNumber: 16384,
        analysisLines: 0
      },
      // 显示设为基准数据按钮的路由列表
      showSaveChartButtonRoutes: [
        '/RotatingMachineryGraph/RotatingVibrationMonitor',
        '/RotatingMachineryGraph/AlarmQueryGraph2',
        '/RotatingMachineryGraph/RotatingCepstrumGraph',
        '/RotatingMachineryGraph/ImpactDiagnosis',
        '/RotatingMachineryGraph/OrbitOfShaftCenter1',
        '/RotatingMachineryGraph/StartStopGraph',
        '/ReciprocatMachineryGraph/ReciprocatAlarmQuery',
        '/ReciprocatMachineryGraph/ReciprocatVibrationMonitor',
        '/ReciprocatMachineryGraph/PistonRodSettlement',
        '/ReciprocatMachineryGraph/ReciprocatImpactDiagnosis',
        '/DieselMonitorSystem/AlarmQuery',
        '/DieselMonitorSystem/VibrationMonitorGraph',
        'AlarmQueryGraph',
        'TrendAnalysis',
        'PumpImpactDiagnosis',
        'CepstrumGraph',
        'WirelessPumpGraph/WirelessPumpAlarmQueryGraph',
        'WirelessPumpGraph/WirelessPumpTrendAnalysis',
        'WirelessPumpGraph/WirelessPumpImpactDiagnosis',
        'WirelessPumpGraph/WirelessPumpCepstrumGraph'
      ],
      // 最多选择的样本数
      maxSamples: 4,
      // 角度支持配置
      angleSupport: {
        enabled: false,
        range: 360
      },
      // 测点变化监听相关
      comparisonEnabled: false,
      lastPointIdString: null,
      lastEquipmentUuid: null,
      selectedSample: null,
      isRefreshingSample: false  // 标记是否正在刷新样本数据
    }
  },
  mounted() {
  },
  methods: {
    /**
     * 检查当前路由是否在显示按钮的路由列表中（使用完整路径匹配）
     */
    shouldShowSaveChartButton() {
      if (!this.$route) return false;
      return this.showSaveChartButtonRoutes.some(route => {
        return this.$route.path === route;
      });
    },

    /**
     * 设置测点变化监听
     * 在组件mounted时调用，用于初始化测点监听
     */
    setupPointMonitoring() {
      if (!this.$route) return;

      const routePath = this.$route.path;
      const shouldMonitor = this.showSaveChartButtonRoutes.some(route => {
        return routePath.indexOf(route) !== -1;
      });

      if (shouldMonitor) {
        this.$nextTick(() => {
          this.initializePointMonitoring();
        });
      }
    },

    /**
     * 初始化测点监听
     * 记录初始测点信息
     */
    initializePointMonitoring() {
      let pointIdString = null;
      let equipmentUuid = null;

      if (this.requestWaveData && this.requestWaveData.pointIdString) {
        pointIdString = this.requestWaveData.pointIdString;
        equipmentUuid = this.requestWaveData.equipmentUuid;
      } else if (this.requestSpectrumData && this.requestSpectrumData.pointIdString) {
        pointIdString = this.requestSpectrumData.pointIdString;
        equipmentUuid = this.requestSpectrumData.equipmentUuid;
      }

      this.lastPointIdString = pointIdString;
      this.lastEquipmentUuid = equipmentUuid;
    },

    /**
     * 检查测点是否发生变化
     * @returns {boolean} true表示测点发生变化
     */
    checkPointChange() {
      if (!this.comparisonEnabled) {
        return false;
      }

      let currentPointIdString = null;
      let currentEquipmentUuid = null;

      if (this.requestWaveData && this.requestWaveData.pointIdString) {
        currentPointIdString = this.requestWaveData.pointIdString;
        currentEquipmentUuid = this.requestWaveData.equipmentUuid;
      } else if (this.requestSpectrumData && this.requestSpectrumData.pointIdString) {
        currentPointIdString = this.requestSpectrumData.pointIdString;
        currentEquipmentUuid = this.requestSpectrumData.equipmentUuid;
      } else if (this.requestWaveData && this.requestWaveData.pointUuid) {
        currentPointIdString = this.requestWaveData.pointUuid;
        currentEquipmentUuid = this.requestWaveData.equipmentUuid;
      }

      const pointChanged = currentPointIdString !== this.lastPointIdString;
      const equipmentChanged = currentEquipmentUuid !== this.lastEquipmentUuid;

      if (pointChanged || equipmentChanged) {
        console.log('【测点变化监听】检测到测点变化:');
        if (pointChanged) {
          console.log('  - 测点ID变化:', this.lastPointIdString, '->', currentPointIdString);
        }
        if (equipmentChanged) {
          console.log('  - 设备UUID变化:', this.lastEquipmentUuid, '->', currentEquipmentUuid);
        }

        this.lastPointIdString = currentPointIdString;
        this.lastEquipmentUuid = currentEquipmentUuid;

        return true;
      }

      return false;
    },

    /**
     * 当测点变化时刷新样本数据
     * 使用新测点重新获取样本数据并绘制
     */
    async refreshSampleDataOnPointChange() {
      if (!this.comparisonEnabled) {
        console.log('【测点变化监听】对比功能未开启，不刷新样本数据');
        return;
      }

      if (!this.selectedSample) {
        console.log('【测点变化监听】没有选中的样本，不刷新');
        return;
      }

      console.log('【测点变化监听】开始刷新样本数据...');

      try {
        // 设置刷新标志，防止原始数据绘制时自动绘制旧样本数据
        this.isRefreshingSample = true;

        // 先清除旧的样本数据（包括图表和内存中的数据）
        console.log('【测点变化监听】清除旧样本数据...');
        if (this.$refs.waveg && this.$refs.waveg.clearSampleData) {
          this.$refs.waveg.clearSampleData();
        }
        if (this.$refs.spectrumg && this.$refs.spectrumg.clearSampleData) {
          this.$refs.spectrumg.clearSampleData();
        }

        // 清除内存中的样本数据，防止重新绘制时显示旧数据
        this.sampleWaveData = null;
        this.sampleSpectrumData = null;

        // 重新获取并绘制样本数据
        await this.handleSampleSelected(this.selectedSample);

        // 刷新完成，清除标志
        this.isRefreshingSample = false;

        console.log('【测点变化监听】样本数据刷新成功');
      } catch (error) {
        console.error('【测点变化监听】样本数据刷新失败:', error);
        this.isRefreshingSample = false;
      }
    },

    /**
     * 当测点变化时刷新 ImpactDiagnosis 组件的样本数据
     * 专门用于 ImpactDiagnosis 组件，包含波形、频谱、包络波形、包络频谱
     */
    async refreshImpactDiagnosisSampleDataOnPointChange() {
      if (!this.comparisonEnabled) {
        console.log('【测点变化监听-ImpactDiagnosis】对比功能未开启，不刷新样本数据');
        return;
      }

      if (!this.selectedSample) {
        console.log('【测点变化监听-ImpactDiagnosis】没有选中的样本，不刷新');
        return;
      }

      console.log('【测点变化监听-ImpactDiagnosis】开始刷新样本数据...');

      try {
        // 设置刷新标志，防止原始数据绘制时自动绘制旧样本数据
        this.isRefreshingSample = true;

        // 先清除旧的样本数据（包括图表和内存中的数据）
        console.log('【测点变化监听-ImpactDiagnosis】清除旧样本数据...');
        if (this.$refs.waveg && this.$refs.waveg.clearSampleData) {
          this.$refs.waveg.clearSampleData();
        }
        if (this.$refs.spectrumg && this.$refs.spectrumg.clearSampleData) {
          this.$refs.spectrumg.clearSampleData();
        }
        if (this.$refs.envelopeWaveG && this.$refs.envelopeWaveG.clearSampleData) {
          this.$refs.envelopeWaveG.clearSampleData();
        }
        if (this.$refs.envelopeSpectrumG && this.$refs.envelopeSpectrumG.clearSampleData) {
          this.$refs.envelopeSpectrumG.clearSampleData();
        }

        // 清除内存中的样本数据，防止重新绘制时显示旧数据
        this.sampleWaveData = null;
        this.sampleSpectrumData = null;
        this.sampleEnvelopeWaveData = null;
        this.sampleEnvelopeSpectrumData = null;

        // 使用 handleImpactDiagnosisSampleSelected 重新获取样本数据
        const envelopeType = this.envelopeType || 0;
        const cutoffFrequency = this.cutoffFrequency || 1000;
        const cutoffFrequency1 = this.cutoffFrequency1 || null;
        const cutoffFrequency2 = this.cutoffFrequency2 || null;

        await this.handleImpactDiagnosisSampleSelected(
          this.selectedSample,
          envelopeType,
          cutoffFrequency,
          cutoffFrequency1,
          cutoffFrequency2
        );

        // 刷新完成，清除标志
        this.isRefreshingSample = false;

        console.log('【测点变化监听-ImpactDiagnosis】样本数据刷新成功');
      } catch (error) {
        console.error('【测点变化监听-ImpactDiagnosis】样本数据刷新失败:', error);
        this.isRefreshingSample = false;
      }
    },

    /**
     * 当测点变化时刷新倒谱图组件的样本数据
     * 专门用于倒谱图组件，包含波形图和倒谱图
     */
    async refreshCepstrumSampleDataOnPointChange() {
      if (!this.comparisonEnabled) {
        console.log('【测点变化监听-Cepstrum】对比功能未开启，不刷新样本数据');
        return;
      }

      if (!this.selectedSample) {
        console.log('【测点变化监听-Cepstrum】没有选中的样本，不刷新');
        return;
      }

      console.log('【测点变化监听-Cepstrum】开始刷新样本数据...');

      try {
        // 设置刷新标志，防止原始数据绘制时自动绘制旧样本数据
        this.isRefreshingSample = true;

        // 先清除旧的样本数据（包括图表和内存中的数据）
        console.log('【测点变化监听-Cepstrum】清除旧样本数据...');
        if (this.$refs.waveg && this.$refs.waveg.clearSampleData) {
          this.$refs.waveg.clearSampleData();
        }
        if (this.$refs.spectrumg && this.$refs.spectrumg.clearSampleData) {
          this.$refs.spectrumg.clearSampleData();
        }

        // 清除内存中的样本数据，防止重新绘制时显示旧数据
        this.sampleWaveData = null;
        this.sampleCepstrumData = null;

        // 使用 handleCepstrumSampleSelected 重新获取样本数据
        await this.handleCepstrumSampleSelected(this.selectedSample);

        // 刷新完成，清除标志
        this.isRefreshingSample = false;

        console.log('【测点变化监听-Cepstrum】样本数据刷新成功');
      } catch (error) {
        console.error('【测点变化监听-Cepstrum】样本数据刷新失败:', error);
        this.isRefreshingSample = false;
      }
    },

    /**
     * 启用对比功能
     * 启用后开始监听测点变化
     */
    enableComparison() {
      this.comparisonEnabled = true;
      console.log('【测点变化监听】对比功能已启用，开始监听测点变化');
      this.initializePointMonitoring();
    },

    /**
     * 禁用对比功能
     * 禁用后停止监听测点变化
     */
    disableComparison() {
      this.comparisonEnabled = false;
      this.lastPointIdString = null;
      this.lastEquipmentUuid = null;
      console.log('【测点变化监听】对比功能已禁用，停止监听测点变化');
    },
    /**
     * 处理样本选择事件
     */
    handleSampleSelected(sample) {
      if (!sample) {
        console.error('样本数据无效');
        return;
      }
      if (!sample.info) {
        console.error('样本数据缺少info对象');
        return;
      }

      const equipmentUuid = sample.equipmentUuid;
      let pointIdString;

      // 固定使用原始数据的测点
      if (this.requestWaveData && this.requestWaveData.pointIdString) {
        pointIdString = this.requestWaveData.pointIdString;
      } else if (this.requestSpectrumData && this.requestSpectrumData.pointIdString) {
        pointIdString = this.requestSpectrumData.pointIdString;
      }  else {
        console.log('未找到原始数据测点信息，无法进行对比');
        return;
      }

      if (!equipmentUuid || !pointIdString) {
        console.error('样本数据缺少设备或测点信息');
        return;
      }
      const markTime = sample.info.trendTime;

      if (!markTime) {
        console.error('样本数据缺少时间戳');
        return;
      }
      const waveNumber = sample.info.waveNumber || this.defaultValues.waveNumber;
      const analysisLines = sample.info.analysisLines || this.defaultValues.analysisLines;


      const equipmentUuid1 = encodeURIComponent(equipmentUuid);
      const pointIdString1 = encodeURIComponent(pointIdString);
      const url = `/wave-spectrum/${equipmentUuid1}/${pointIdString1}/${markTime}/${waveNumber}/${analysisLines}/info`;

      this.$axios.get(url).then(res => {
        if (res.data.code === 200) {
          // 保存选中的样本
          this.selectedSample = sample;

          // 获取波形和频谱数据
          const waveData = res.data.data.waveValue;
          const spectrumData = res.data.data.spectrumValue;

          if (waveData && spectrumData) {
            // 保存样本数据
            this.sampleWaveData = waveData;
            this.sampleSpectrumData = spectrumData;

            // 直接更新波形图的Y轴范围，考虑样本数据的最大值
            const waveg = this.$refs.waveg;
            if (waveg && waveg.mychart) {
              // 获取当前图表配置
              const currentOption = waveg.mychart.getOption();
              // 检查是否有原始数据系列
              const hasOriginalSeries = currentOption.series && currentOption.series.some(series => !series.isSampleSeries);
              if (hasOriginalSeries) {
                // 获取原始数据的Y轴数据
                let myYmax, myYmin;
                for (let i = 0; i < currentOption.series.length; i++) {
                  const series = currentOption.series[i];
                  if (!series.isSampleSeries) {
                    for (let j = 0; j < series.data.length; j++) {
                      const yValue = series.data[j][1];
                      if (j === 0) {
                        myYmax = yValue;
                        myYmin = yValue;
                      } else {
                        myYmax = Math.max(myYmax, yValue);
                        myYmin = Math.min(myYmin, yValue);
                      }
                    }
                    break;
                  }
                }

                // 计算新的Y轴范围
                let roundedYMax = waveg.roundUp(myYmin, myYmax);
                let roundedYMin = waveg.roundDown(myYmin, myYmax);

                // 检查是否存在样本数据，如果存在，获取样本数据的最大值
                if (waveData && waveData.waveY) {
                  const sampleYData = waveData.waveY;
                  const sampleMax = Math.max(...sampleYData.filter(val => val !== undefined && val !== null));

                  // 如果样本数据的最大值大于原始数据的最大值，使用样本数据的最大值
                  if (sampleMax > myYmax) {
                    roundedYMax = waveg.roundUp(roundedYMin, sampleMax);
                    if (roundedYMin < 0) {
                      const absMax = Math.max(Math.abs(roundedYMin), Math.abs(roundedYMax));
                      roundedYMax = absMax;
                      roundedYMin = -absMax;
                    }
                  }
                }

                // 调整Y轴范围为对称范围
                if (roundedYMin < 0 && roundedYMax > 0) {
                  const absMax = Math.max(Math.abs(roundedYMin), Math.abs(roundedYMax));
                  roundedYMax = absMax;
                  roundedYMin = -absMax;
                }

                // 更新Y轴范围
                waveg.mychart.setOption({
                  yAxis: {
                    min: roundedYMin,
                    max: roundedYMax
                  }
                });
              }
            }

            // 绘制样本数据
            this.$refs.waveg.drawSampleWaveData(waveData, sample.name);
            // 传递给频谱图组件
            this.$refs.spectrumg.drawSampleSpectrumgData(spectrumData, sample.name);
          } else {
            console.error('响应数据缺少波形或频谱数据');
          }
        } else {
          console.error(`获取样本数据失败：${res.data.message}`);
        }
      }).catch(error => {
        console.error('获取样本数据失败：网络请求错误');
      });
    },

    /**
     * 处理倒谱样本选择事件
     */
    handleCepstrumSampleSelected(sample) {
      if (!sample) {
        console.error('样本数据无效');
        return;
      }
      if (!sample.info) {
        console.error('样本数据缺少info对象');
        return;
      }
      const equipmentUuid = sample.equipmentUuid;
      const pointUuid = this.nowpointuuid;

      if (!equipmentUuid || !pointUuid) {
        console.error('样本数据缺少设备或测点信息');
        return;
      }

      const markTime = sample.info.trendTime || sample.markTime;

      if (!markTime) {
        console.error('样本数据缺少时间戳');
        return;
      }
      const waveNumber = sample.info.waveNumber || this.defaultValues.waveNumber;

      const url = `/Cepstrum/${pointUuid}/${markTime}/${waveNumber}/CepstrumHis`;

      this.$axios.get(url).then(res => {
        if (res.data.code === 200) {
          // 获取波形和倒谱数据
          const waveData = res.data.data.waveValue;
          const cepstrumData = res.data.data.spectrumValue;

          if (waveData && cepstrumData) {
            // 保存样本数据
            this.sampleWaveData = waveData;
            this.sampleCepstrumData = cepstrumData;

            // 直接更新波形图的Y轴范围，考虑样本数据的最大值
            const waveg = this.$refs.waveg;
            if (waveg && waveg.mychart) {
              // 获取当前图表配置
              const currentOption = waveg.mychart.getOption();
              // 检查是否有原始数据系列
              const hasOriginalSeries = currentOption.series && currentOption.series.some(series => !series.isSampleSeries);
              if (hasOriginalSeries) {
                // 获取原始数据的Y轴数据
                let myYmax, myYmin;
                for (let i = 0; i < currentOption.series.length; i++) {
                  const series = currentOption.series[i];
                  if (!series.isSampleSeries) {
                    for (let j = 0; j < series.data.length; j++) {
                      const yValue = series.data[j][1];
                      if (j === 0) {
                        myYmax = yValue;
                        myYmin = yValue;
                      } else {
                        myYmax = Math.max(myYmax, yValue);
                        myYmin = Math.min(myYmin, yValue);
                      }
                    }
                    break;
                  }
                }

                // 计算新的Y轴范围
                let roundedYMax = waveg.roundUp(myYmin, myYmax);
                let roundedYMin = waveg.roundDown(myYmin, myYmax);

                // 检查是否存在样本数据，如果存在，获取样本数据的最大值
                if (waveData && waveData.waveY) {
                  const sampleYData = waveData.waveY;
                  const sampleMax = Math.max(...sampleYData.filter(val => val !== undefined && val !== null));

                  // 如果样本数据的最大值大于原始数据的最大值，使用样本数据的最大值
                  if (sampleMax > myYmax) {
                    roundedYMax = waveg.roundUp(roundedYMin, sampleMax);
                    if (roundedYMin < 0) {
                      const absMax = Math.max(Math.abs(roundedYMin), Math.abs(roundedYMax));
                      roundedYMax = absMax;
                      roundedYMin = -absMax;
                    }
                  }
                }

                // 调整Y轴范围为对称范围
                if (roundedYMin < 0 && roundedYMax > 0) {
                  const absMax = Math.max(Math.abs(roundedYMin), Math.abs(roundedYMax));
                  roundedYMax = absMax;
                  roundedYMin = -absMax;
                }

                // 更新Y轴范围
                waveg.mychart.setOption({
                  yAxis: {
                    min: roundedYMin,
                    max: roundedYMax
                  }
                });
              }
            }

            // 绘制样本数据
            this.$refs.waveg.drawSampleWaveData(waveData, sample.name);
            // 传递给倒谱图组件
            this.$refs.spectrumg.drawSampleCepstrumData(cepstrumData, sample.name);
          } else {
            console.error('响应数据缺少波形或倒谱数据');
          }
        } else {
          console.error(`获取倒谱样本数据失败：${res.data.message || '未知错误'}`);
        }
      }).catch(error => {
        console.error('获取样本数据失败：网络请求错误');
      });
    },
    /**
     * 绘制倒谱样本数据
     */
    drawSampleCepstrumData(sampleData, sampleName) {
      if (!this.mychart) return;
      const currentOption = this.mychart.getOption();

      const hasOriginalSeries = currentOption.series && currentOption.series.some(series => !series.isSampleSeries);
      if (!hasOriginalSeries) {
        // 如果没有原始数据系列，保存样本数据但不绘制
        this.sampleCepstrumData = sampleData;
        this.sampleName = sampleName;
        return;
      }

      this.sampleCepstrumData = sampleData;
      this.sampleName = sampleName;

      let seriesData = [];

      for (let i = 0; i < sampleData.spectrumX.length; i++) {
        if (sampleData.spectrumY[i] !== undefined) {
          seriesData.push([sampleData.spectrumX[i], sampleData.spectrumY[i]]);
        }
      }
      const sampleSeries = {
        name: sampleName,
        type: 'line',
        data: seriesData,
        xAxisIndex: 0,
        yAxisIndex: 0,
        isSampleSeries: true,
        showAllSymbol: this.sampleConfig.showAllSymbol,
        showSymbol: this.sampleConfig.showSymbol,
        color: this.sampleConfig.color,
        z: this.sampleConfig.zIndex,
        animation: this.sampleConfig.animation,
        itemStyle: {
          normal: {
            lineStyle: {
              color: this.sampleConfig.color,
              width: this.sampleConfig.lineWidth,
              opacity: 1,
            }
          }
        },
        emphasis: {
          focus: 'series'
        },
      };

      const originalSeries = currentOption.series.filter(function(series) {
        return !series.isSampleSeries;
      });

      originalSeries.forEach(function(series) {
        series.z = this.originalConfig.zIndex;
      }.bind(this));

      const sampleSeriesIndex = currentOption.series.findIndex(function(series) {
        return series.isSampleSeries;
      });
      if (sampleSeriesIndex > -1) {
        currentOption.series[sampleSeriesIndex] = sampleSeries;
      } else {
        currentOption.series.unshift(sampleSeries);
      }

      const updatedOriginalSeries = currentOption.series.filter(function(series) {
        return !series.isSampleSeries;
      });
      const sampleSeriesList = currentOption.series.filter(function(series) {
        return series.isSampleSeries;
      });
      currentOption.series = sampleSeriesList.concat(updatedOriginalSeries);

      let legendConfig;
      if (!currentOption.legend || (Array.isArray(currentOption.legend) && currentOption.legend.length === 0)) {
        legendConfig = {
          type: this.legendConfig.type,
          pageIconColor: this.$store.state.selectbordercolor,
          pageIconInactiveColor: this.$store.state.selectbordercolor,
          pageTextStyle: {
            color: this.$store.state.selectbordercolor
          },
          left: this.legendConfig.left,
          bottom: this.legendConfig.bottom,
          data: [],
          itemWidth: this.legendConfig.itemWidth,
          itemHeight: this.legendConfig.itemHeight,
          itemGap: this.legendConfig.itemGap,
          formatter: function (name) {
            return '{a|' + name + '}';
          },
          textStyle: {
            rich: {
              a: {
                verticalAlign: 'middle',
                lineHeight: 12,
                fontSize: 12,
                color: this.sampleConfig.color,
                padding: [0, 0, 3, 4],
              }
            }
          }
        };
        currentOption.legend = [legendConfig];
      } else {
        legendConfig = Array.isArray(currentOption.legend) ? currentOption.legend[0] : currentOption.legend;
      }

      const orderedSeries = originalSeries.concat(sampleSeriesList);

      const defectFreqName = this.translateText ? this.translateText('textMap.', '缺陷频率') : '缺陷频率';
      const bladeFreqName = this.translateText ? this.translateText('textMap.', '叶片频率') : '叶片频率';

      legendConfig.data = orderedSeries.map(function(series) {
        return {
          name: series.name,
          icon: 'rect',
        };
      }).filter(function(item) {
        return item.name !== defectFreqName && item.name !== bladeFreqName;
      });

      if (this.legendData) {
        this.legendData = currentOption.series.map(function(series) {
          return series.name;
        }).filter(function(name) {
          return name !== defectFreqName && name !== bladeFreqName;
        });
      }

      if (sampleData && sampleData.spectrumY) {
        const sampleYData = sampleData.spectrumY;
        const sampleMax = Math.max(...sampleYData.filter(val => val !== undefined && val !== null));

        let originalMax = Number.NEGATIVE_INFINITY;
        let originalMin = Number.POSITIVE_INFINITY;

        originalSeries.forEach(series => {
          if (series.data) {
            series.data.forEach(point => {
              if (point && point[1] !== undefined && point[1] !== null) {
                originalMax = Math.max(originalMax, point[1]);
                originalMin = Math.min(originalMin, point[1]);
              }
            });
          }
        });

        const combinedMax = Math.max(originalMax, sampleMax);
        const combinedMin = originalMin;

        let roundedYMax = this.roundUp(combinedMin, combinedMax);
        let roundedYMin = this.roundDown(combinedMin, combinedMax);

        if (roundedYMin < 0 && roundedYMax > 0) {
          const absMax = Math.max(Math.abs(roundedYMin), Math.abs(roundedYMax));
          roundedYMax = absMax;
          roundedYMin = -absMax;
        }

        currentOption.yAxis[0].max = roundedYMax;
        currentOption.yAxis[0].min = roundedYMin;
      }

      this.mychart.setOption(currentOption);
    },

    /**
     * 绘制频谱样本数据
     */
    drawSampleSpectrumgData(sampleData, sampleName) {
      if (!this.mychart) return;
      const currentOption = this.mychart.getOption();

      const hasOriginalSeries = currentOption.series && currentOption.series.some(function(series) {
        return !series.isSampleSeries;
      });
      if (!hasOriginalSeries) {
        // 如果没有原始数据系列，保存样本数据但不绘制
        this.sampleSpectrumData = sampleData;
        this.sampleName = sampleName;
        return;
      }

      this.sampleSpectrumData = sampleData;
      this.sampleName = sampleName;

      let seriesData = [];

      let xDataToUse = sampleData.spectrumX;
      const isOrder = this.changeX && this.changeX.state === 'Order';
      const oneFrequencyValue = this.changeX && this.changeX.oneFrequencyValue;

      if (isOrder && oneFrequencyValue && oneFrequencyValue > 0) {
        xDataToUse = sampleData.spectrumX.map(x => x / oneFrequencyValue);
      }

      for (let i = 0; i < xDataToUse.length; i++) {
        if (sampleData.spectrumY[i] !== undefined) {
          let xValue = xDataToUse[i];
          if (isOrder) {
            xValue = this.roundToDecimalPlaces(parseFloat(xValue), 2);
          }
          seriesData.push([xValue, sampleData.spectrumY[i]]);
        }
      }
      const sampleSeries = {
        name: sampleName,
        type: 'line',
        data: seriesData,
        xAxisIndex: 0,
        yAxisIndex: 0,
        isSampleSeries: true,
        showAllSymbol: this.sampleConfig.showAllSymbol,
        showSymbol: this.sampleConfig.showSymbol,
        color: this.sampleConfig.color,
        z: this.sampleConfig.zIndex,
        animation: this.sampleConfig.animation,
        itemStyle: {
          normal: {
            lineStyle: {
              color: this.sampleConfig.color,
              width: this.sampleConfig.lineWidth,
              opacity: 1,
            }
          }
        },
        emphasis: {
          focus: 'series'
        },
      };

      const originalSeries = currentOption.series.filter(function(series) {
        return !series.isSampleSeries;
      });

      originalSeries.forEach(function(series) {
        series.z = this.originalConfig.zIndex;
      }.bind(this));

      const sampleSeriesIndex = currentOption.series.findIndex(function(series) {
        return series.isSampleSeries;
      });
      if (sampleSeriesIndex > -1) {
        currentOption.series[sampleSeriesIndex] = sampleSeries;
      } else {
        currentOption.series.unshift(sampleSeries);
      }

      const updatedOriginalSeries = currentOption.series.filter(function(series) {
        return !series.isSampleSeries;
      });
      const sampleSeriesList = currentOption.series.filter(function(series) {
        return series.isSampleSeries;
      });
      currentOption.series = sampleSeriesList.concat(updatedOriginalSeries);

      let legendConfig;
      if (!currentOption.legend || (Array.isArray(currentOption.legend) && currentOption.legend.length === 0)) {
        legendConfig = {
          type: this.legendConfig.type,
          pageIconColor: this.$store.state.selectbordercolor,
          pageIconInactiveColor: this.$store.state.selectbordercolor,
          pageTextStyle: {
            color: this.$store.state.selectbordercolor
          },
          left: this.legendConfig.left,
          bottom: this.legendConfig.bottom,
          data: [],
          itemWidth: this.legendConfig.itemWidth,
          itemHeight: this.legendConfig.itemHeight,
          itemGap: this.legendConfig.itemGap,
          formatter: function (name) {
            return '{a|' + name + '}';
          },
          textStyle: {
            rich: {
              a: {
                verticalAlign: 'middle',
                lineHeight: 12,
                fontSize: 12,
                color: this.sampleConfig.color,
                padding: [0, 0, 3, 4],
              }
            }
          }
        };
        currentOption.legend = [legendConfig];
      } else {
        legendConfig = Array.isArray(currentOption.legend) ? currentOption.legend[0] : currentOption.legend;
      }

      const orderedSeries = originalSeries.concat(sampleSeriesList);

      const defectFreqName = this.translateText ? this.translateText('textMap.', '缺陷频率') : '缺陷频率';
      const bladeFreqName = this.translateText ? this.translateText('textMap.', '叶片频率') : '叶片频率';
      const namesToFilter = [defectFreqName, bladeFreqName, 'bearingDefect', 'data1'];
      legendConfig.data = orderedSeries.map(function(series) {
        return {
          name: series.name,
          icon: 'rect',
        };
      }).filter(function(item) {
        return !namesToFilter.includes(item.name);
      });

      if (this.legendData) {
        this.legendData = currentOption.series.map(function(series) {
          return series.name;
        }).filter(function(name) {
          return !namesToFilter.includes(name);
        });
      }

      if (sampleData && sampleData.spectrumY) {
        const sampleYData = sampleData.spectrumY;
        const sampleMax = Math.max(...sampleYData.filter(val => val !== undefined && val !== null));

        let originalMax = Number.NEGATIVE_INFINITY;
        let originalMin = Number.POSITIVE_INFINITY;

        originalSeries.forEach(series => {
          if (series.data) {
            series.data.forEach(point => {
              if (point && point[1] !== undefined && point[1] !== null) {
                originalMax = Math.max(originalMax, point[1]);
                originalMin = Math.min(originalMin, point[1]);
              }
            });
          }
        });

        const combinedMax = Math.max(originalMax, sampleMax);
        const combinedMin = originalMin;

        let roundedYMax = this.roundUp(combinedMin, combinedMax);
        let roundedYMin = this.roundDown(combinedMin, combinedMax);

        if (roundedYMin < 0 && roundedYMax > 0) {
          const absMax = Math.max(Math.abs(roundedYMin), Math.abs(roundedYMax));
          roundedYMax = absMax;
          roundedYMin = -absMax;
        }

        currentOption.yAxis[0].max = roundedYMax;
        currentOption.yAxis[0].min = roundedYMin;
      }

      this.mychart.setOption(currentOption);
    },

    /**
     * 绘制波形样本数据
     */
    drawSampleWaveData(sampleData, sampleName) {
      if (!this.mychart) return;
      this.sampleWaveData = sampleData;
      this.sampleName = sampleName;

      const currentOption = this.mychart.getOption();

      const hasOriginalSeries = currentOption.series && currentOption.series.some(function(series) {
        return !series.isSampleSeries;
      });
      if (!hasOriginalSeries) {
        return;
      }

      let seriesData = [];
      let sampleWaveX = sampleData.waveX;

      // 检查当前是否处于角度模式，如果是，使用角度数据
      if (this.changeX && this.changeX.xType === 'angle') {
        if (this.angleXData && this.angleXData.length > 0) {
          sampleWaveX = this.angleXData;
        }
      }

      for (let i = 0; i < sampleWaveX.length; i++) {
        if (sampleData.waveY[i] !== undefined) {
          seriesData.push([sampleWaveX[i], sampleData.waveY[i]]);
        }
      }

      const sampleSeries = {
        name: sampleName,
        type: 'line',
        data: seriesData,
        xAxisIndex: 0,
        yAxisIndex: 0,
        isSampleSeries: true,
        showAllSymbol: this.sampleConfig.showAllSymbol,
        showSymbol: this.sampleConfig.showSymbol,
        color: this.sampleConfig.color,
        z: this.sampleConfig.zIndex,
        animation: this.sampleConfig.animation,
        itemStyle: {
          normal: {
            lineStyle: {
              color: this.sampleConfig.color,
              width: this.sampleConfig.lineWidth,
              opacity: 1,
            }
          }
        },
        emphasis: {
          focus: 'series'
        },
      };

      const originalSeries = currentOption.series.filter(function(series) {
        return !series.isSampleSeries;
      });

      originalSeries.forEach(function(series) {
        series.z = this.originalConfig.zIndex;
      }.bind(this));

      const sampleSeriesIndex = currentOption.series.findIndex(function(series) {
        return series.isSampleSeries;
      });
      if (sampleSeriesIndex > -1) {
        currentOption.series[sampleSeriesIndex] = sampleSeries;
      } else {
        currentOption.series.unshift(sampleSeries);
      }

      const updatedOriginalSeries = currentOption.series.filter(function(series) {
        return !series.isSampleSeries;
      });
      const sampleSeriesList = currentOption.series.filter(function(series) {
        return series.isSampleSeries;
      });

      currentOption.series = sampleSeriesList.concat(updatedOriginalSeries);

      if (this.legendData) {
        this.legendData = currentOption.series.map(function(series) {
          return series.name;
        });
      }

      let legendConfig;
      if (!currentOption.legend || (Array.isArray(currentOption.legend) && currentOption.legend.length === 0)) {
        legendConfig = {
          type: this.legendConfig.type,
          pageIconColor: this.$store.state.selectbordercolor,
          pageIconInactiveColor: this.$store.state.selectbordercolor,
          pageTextStyle: {
            color: this.$store.state.selectbordercolor
          },
          left: this.legendConfig.left,
          bottom: this.legendConfig.bottom,
          data: [],
          itemWidth: this.legendConfig.itemWidth,
          itemHeight: this.legendConfig.itemHeight,
          itemGap: this.legendConfig.itemGap,
          formatter: function (name) {
            return '{a|' + name + '}';
          },
          textStyle: {
            rich: {
              a: {
                verticalAlign: 'middle',
                lineHeight: 12,
                fontSize: 12,
                color: this.sampleConfig.color,
                padding: [0, 0, 3, 4],
              }
            }
          }
        };
        currentOption.legend = [legendConfig];
      } else {
        legendConfig = Array.isArray(currentOption.legend) ? currentOption.legend[0] : currentOption.legend;
      }

      const orderedSeries = originalSeries.concat(sampleSeriesList);

      const defectFreqName = this.translateText ? this.translateText('textMap.', '缺陷频率') : '缺陷频率';
      const bladeFreqName = this.translateText ? this.translateText('textMap.', '叶片频率') : '叶片频率';

      legendConfig.data = orderedSeries.map(function(series) {
        return {
          name: series.name,
          icon: 'rect',
        };
      }).filter(function(item) {
        return item.name !== defectFreqName && item.name !== bladeFreqName;
      });

      if (sampleData && sampleData.waveY) {
        const sampleYData = sampleData.waveY;
        const sampleMax = Math.max(...sampleYData.filter(val => val !== undefined && val !== null));

        let originalMax = Number.NEGATIVE_INFINITY;
        let originalMin = Number.POSITIVE_INFINITY;

        originalSeries.forEach(series => {
          if (series.data) {
            series.data.forEach(point => {
              if (point && point[1] !== undefined && point[1] !== null) {
                originalMax = Math.max(originalMax, point[1]);
                originalMin = Math.min(originalMin, point[1]);
              }
            });
          }
        });

        const combinedMax = Math.max(originalMax, sampleMax);
        const combinedMin = originalMin;

        let roundedYMax = this.roundUp(combinedMin, combinedMax);
        let roundedYMin = this.roundDown(combinedMin, combinedMax);

        if (roundedYMin < 0 && roundedYMax > 0) {
          const absMax = Math.max(Math.abs(roundedYMin), Math.abs(roundedYMax));
          roundedYMax = absMax;
          roundedYMin = -absMax;
        }

        currentOption.yAxis[0].max = roundedYMax;
        currentOption.yAxis[0].min = roundedYMin;
      }

      // 只更新 series 和 legend，不更新整个 option
      this.mychart.setOption({
        series: currentOption.series,
        legend: currentOption.legend,
        yAxis: currentOption.yAxis
      });
    },

    /**
     * 清除样本数据
     */
    clearSampleData() {
      if (!this.mychart) return;
      const currentOption = this.mychart.getOption();

      if (currentOption.series) {
        currentOption.series.forEach((series, index) => {
          console.log(`Series ${index}:`, {
            name: series.name,
            isSampleSeries: series.isSampleSeries,
            type: series.type
          });
        });
      }

      const filteredSeries = currentOption.series.filter(function(series) {
        return !series.isSampleSeries;
      });

      const defectFreqName = this.translateText ? this.translateText('textMap.', '缺陷频率') : '缺陷频率';
      const bladeFreqName = this.translateText ? this.translateText('textMap.', '叶片频率') : '叶片频率';

      if (filteredSeries.length !== currentOption.series.length) {
        if (this.legendData) {
          this.legendData = filteredSeries.map(function(series) {
            return series.name;
          }).filter(function(name) {
            return name !== defectFreqName && name !== bladeFreqName;
          });
        }

        currentOption.series = filteredSeries;
        if (currentOption.legend) {
          currentOption.legend.data = filteredSeries.map(function(series) {
            return series.name;
          }).filter(function(name) {
            return name !== defectFreqName && name !== bladeFreqName;
          });
        }

        const { toolbox, ...optionWithoutToolbox } = currentOption;

        this.mychart.setOption(optionWithoutToolbox, { notMerge: true });

        if (toolbox) {
          this.mychart.setOption({ toolbox });
        }
      }
      this.sampleWaveData = null;
      this.sampleSpectrumData = null;
      this.sampleName = null;
    },

    /**
     * 处理冲击诊断样本选择事件（波形、包络波形、频谱、包络频谱）
     */
    handleImpactDiagnosisSampleSelected(sample, envelopeType = 0, cutoffFrequency = 1000, cutoffFrequency1 = null, cutoffFrequency2 = null) {
      if (!sample) {
        console.error('样本数据无效');
        return;
      }
      if (!sample.info) {
        console.error('样本数据缺少info对象');
        return;
      }

      const equipmentUuid = this.nowpointid || sample.equipmentUuid;
      let pointIdString;

      if (this.tongdao === 0 && this.vibPointIdString) {
        pointIdString = this.vibPointIdString;
      } else if (this.tongdao === 1 && this.dynPointIdString) {
        pointIdString = this.dynPointIdString;
      } else if (this.tongdao === 2 && this.procPointIdString) {
        pointIdString = this.procPointIdString;
      } else {
        pointIdString = this.pointIdString || sample.info.pointIdString || sample.info.pointname;
      }

      const markTime = sample.info.trendTime || sample.markTime;


      if (!equipmentUuid || !pointIdString) {
        console.error('样本数据缺少设备或测点信息');
        return;
      }

      if (!markTime) {
        console.error('样本数据缺少时间戳');
        return;
      }
      const waveNumber = sample.info.waveNumber || this.defaultValues.waveNumber;
      const analysisLines = sample.info.analysisLines || this.defaultValues.analysisLines;

      const equipmentUuid1 = encodeURIComponent(equipmentUuid);
      let pointIdString1 = encodeURIComponent(encodeURIComponent(pointIdString));


      const waveSpectrumPath = `/wave-spectrum/${equipmentUuid1}/${pointIdString1}/${markTime}/${waveNumber}/${analysisLines}/info`;
      let impactPath = `/impact/${equipmentUuid1}/${pointIdString1}/${markTime}/${waveNumber}/${analysisLines}/info`;
      let impactQuery = envelopeType ? `filterType=${envelopeType}&cutoffFreq=${cutoffFrequency1}&cutoffFreq1=${cutoffFrequency2}` : `filterType=${envelopeType}&cutoffFreq=${cutoffFrequency}`;

      const requests = [this.$axios.get(waveSpectrumPath)];

      if (pointIdString.includes(',')) {
        const firstPointIdString = pointIdString.split(',')[0].trim();
        const firstPointIdString1 = encodeURIComponent(encodeURIComponent(firstPointIdString));
        const firstPointIdString1Encoded = firstPointIdString1;
        impactPath = `/impact/${equipmentUuid1}/${firstPointIdString1Encoded}/${markTime}/${waveNumber}/${analysisLines}/info`;
      }

      requests.push(this.$axios.get(`${impactPath}?${impactQuery}`));

      Promise.all(requests).then(([waveSpectrumRes, impactRes]) => {

        if (waveSpectrumRes.data.code === 200) {
          const originalWaveData = waveSpectrumRes.data.data.waveValue;
          const originalSpectrumData = waveSpectrumRes.data.data.spectrumValue;

          this.sampleWaveData = originalWaveData;
          this.sampleSpectrumData = originalSpectrumData;


          if (this.$refs.waveg) {
            if (typeof this.$refs.waveg.drawSampleWaveData === 'function') {
              this.$refs.waveg.drawSampleWaveData(originalWaveData, sample.name);
            }
          }

          if (this.$refs.spectrumg) {
            if (typeof this.$refs.spectrumg.drawSampleSpectrumgData === 'function') {
              this.$refs.spectrumg.drawSampleSpectrumgData(originalSpectrumData, sample.name);
            }
          }
        }

        if (impactRes.data.code === 200) {
          const envelopeWaveData = impactRes.data.data.waveValue;
          const envelopeSpectrumData = impactRes.data.data.spectrumValue;


          this.sampleEnvelopeWaveData = envelopeWaveData;
          this.sampleEnvelopeSpectrumData = envelopeSpectrumData;


          if (this.$refs.envelopeWaveG) {
            if (typeof this.$refs.envelopeWaveG.drawSampleWaveData === 'function') {
              this.$refs.envelopeWaveG.drawSampleWaveData(envelopeWaveData, sample.name);
            }
          }

          if (this.$refs.envelopeSpectrumG) {
            if (typeof this.$refs.envelopeSpectrumG.drawSampleSpectrumgData === 'function') {
              this.$refs.envelopeSpectrumG.drawSampleSpectrumgData(envelopeSpectrumData, sample.name);
            }
          }
        }
      }).catch(error => {
        console.error('获取样本数据失败：网络请求错误');
      });
    },

    /**
     * 计算Y轴范围，包含样本数据最大值处理逻辑
     */
    calculateYAxisRange(dataY) {
      let myYmax, myYmin;
      for(let item in dataY.data) {
        if(item == 0) {
          myYmax = dataY.data[item];
          myYmin = dataY.data[item];
        } else {
          myYmax = Math.max(myYmax, dataY.data[item]);
          myYmin = Math.min(myYmin, dataY.data[item]);
        }
      }



      let roundedYMax = this.roundUp(myYmin, myYmax);
      let roundedYMin = this.roundDown(myYmin, myYmax);



      // 检查是否存在样本数据，如果存在，获取样本数据的最大值
      if (this.sampleWaveData && this.sampleWaveData.waveY) {
        const sampleYData = this.sampleWaveData.waveY;
        const sampleMax = Math.max(...sampleYData.filter(val => val !== undefined && val !== null));


        if (sampleMax > myYmax) {
          roundedYMax = this.roundUp(roundedYMin, sampleMax);
          if (roundedYMin < 0) {
            const absMax = Math.max(Math.abs(roundedYMin), Math.abs(roundedYMax));
            roundedYMax = absMax;
            roundedYMin = -absMax;
          }
        }
      } else if (this.sampleSpectrumData && this.sampleSpectrumData.spectrumY) {
        const sampleYData = this.sampleSpectrumData.spectrumY;
        const sampleMax = Math.max(...sampleYData.filter(val => val !== undefined && val !== null));

        console.log('【Y轴范围计算】样本数据接口返回的最大y值:', sampleMax);

        if (sampleMax > myYmax) {
          roundedYMax = this.roundUp(roundedYMin, sampleMax);
          if (roundedYMin < 0) {
            const absMax = Math.max(Math.abs(roundedYMin), Math.abs(roundedYMax));
            roundedYMax = absMax;
            roundedYMin = -absMax;
          }

          console.log('【Y轴范围计算】样本数据最大值大于原始数据，使用样本最大值');
          console.log('【Y轴范围计算】调整后Y轴最大值:', roundedYMax);
          console.log('【Y轴范围计算】调整后Y轴最小值:', roundedYMin);
        }
      }

      if (roundedYMin < 0 && roundedYMax > 0) {
        const absMax = Math.max(Math.abs(roundedYMin), Math.abs(roundedYMax));
        roundedYMax = absMax;
        roundedYMin = -absMax;
      }

      console.log('【Y轴范围计算】最终Y轴最大值:', roundedYMax);
      console.log('【Y轴范围计算】最终Y轴最小值:', roundedYMin);

      return { min: roundedYMin, max: roundedYMax };
    },

    /**
     * 处理轴心轨迹样本选择（支持多样本）
     */
    handleOrbitSamplesSelected(samples) {

      if (!samples || samples.length === 0) {
        this.$message.error(this.translateText('textMap.', '请选择样本数据'));
        return;
      }

      if (samples.length > this.maxSamples) {
        this.$message.error(this.translateText('textMap.', '最多只能选择四个样本'));
        return;
      }

      const requests = samples.map((sample, index) => {

        let xPointUuid, yPointUuid, markTime, equipmentUuid, filterType;

        markTime = sample.info.markTime || sample.info.trendTime || sample.markTime;
        equipmentUuid = sample.equipmentUuid;
        filterType = this.filterType !== undefined ? this.filterType : (sample.info.filterType || -1);

        if (this.nowtree && this.nowtree[1]) {
          xPointUuid = this.nowtree[1].xid;
          yPointUuid = this.nowtree[1].yid;
        } else {
          console.error(`样本 ${index + 1} - ${sample.name} 无法找到当前数据的测点对`);
          return Promise.reject(new Error(`样本 ${sample.name} 无法找到当前数据的测点对`));
        }

        if (!xPointUuid || !yPointUuid || !markTime || !equipmentUuid) {
          console.error(`样本 ${index + 1} - ${sample.name} 缺少必要参数:`, { xPointUuid, yPointUuid, markTime, equipmentUuid });
          return Promise.reject(new Error(`样本 ${sample.name} 缺少必要参数`));
        }

        const url = `/axis/${encodeURIComponent(equipmentUuid)}/${encodeURIComponent(xPointUuid)}/${encodeURIComponent(yPointUuid)}/${markTime}/${filterType}/hisAxis`;

        return this.$axios.get(url);
      }).filter(p => p !== undefined);

      if (requests.length === 0) {
        console.error('没有有效的样本数据');
        return;
      }

      Promise.all(requests).then(responses => {

        const samplesData = responses.map((res, index) => {
          if (res.data.code === 200) {
            return {
              name: samples[index].name,
              axisInfo: res.data.data.axisInfo,
              waveXList: res.data.data.waveXList,
              waveYList: res.data.data.waveYList,
              status: res.data.data.status
            };
          }
          console.error(`样本 ${index + 1} 数据获取失败:`, res.data);
          return null;
        }).filter(item => item !== null);

        this.drawMultipleOrbitSamples(samplesData);
      }).catch(error => {
        console.error('获取样本数据失败:', error);
      });
    },

    findPointPairByUuid(pointUuid) {

      if (!this.$store.state.axisList || !this.$store.state.axisList.length) {


        if (!this.$store.state.pointTree || !this.$store.state.pointTree.length) {
          return null;
        }

        for (let i = 0; i < this.$store.state.pointTree.length; i++) {
          const treeNode = this.$store.state.pointTree[i];

          if (treeNode.children && treeNode.children.length > 0) {
            for (let j = 0; j < treeNode.children.length; j++) {
              const childNode = treeNode.children[j];

              if (childNode.xpointUuid === pointUuid) {
                return {
                  xid: childNode.xpointUuid,
                  yid: childNode.ypointUuid,
                  xName: childNode.xName,
                  yName: childNode.yName
                };
              }

              if (childNode.ypointUuid === pointUuid) {
                return {
                  xid: childNode.xpointUuid,
                  yid: childNode.ypointUuid,
                  xName: childNode.xName,
                  yName: childNode.yName
                };
              }
            }
          }
        }

        return null;
      }

      for (let i = 0; i < this.$store.state.axisList.length; i++) {
        const axisPair = this.$store.state.axisList[i];

        if (axisPair.xpointUuid === pointUuid) {
          return {
            xid: axisPair.xpointUuid,
            yid: axisPair.ypointUuid,
            xName: axisPair.xpointName,
            yName: axisPair.ypointName
          };
        }

        if (axisPair.ypointUuid === pointUuid) {
          return {
            xid: axisPair.xpointUuid,
            yid: axisPair.ypointUuid,
            xName: axisPair.xpointName,
            yName: axisPair.ypointName
          };
        }
      }

      return null;
    },

    /**
     * 处理启停车样本选择事件
     * 用于绘制启停车样本数据
     */
    handleStartStopSampleSelected(sample, drawSpectrum = true) {
      if (!sample) {
        console.error('样本数据无效');
        return;
      }
      if (!sample.info) {
        console.error('样本数据缺少info对象');
        return;
      }
      if (this.$refs.spectrumg && typeof this.$refs.spectrumg.clearSampleData === 'function') {
        if (this.$refs.spectrumg.graphType === '倒谱图') {
          this.$refs.spectrumg.clearSampleData();
        }
      }
      if (this.$refs.envelopeWaveG && typeof this.$refs.envelopeWaveG.clearSampleData === 'function') {
        this.$refs.envelopeWaveG.clearSampleData();
      }
      if (this.$refs.envelopeSpectrumG && typeof this.$refs.envelopeSpectrumG.clearSampleData === 'function') {
        this.$refs.envelopeSpectrumG.clearSampleData();
      }

      const equipmentUuid = sample.equipmentUuid;
      const eventId = sample.info.eventId;
      const pointIdString = sample.info.pointIdString || sample.info.pointname;
      const xPointUuid = sample.info.xPointUuid;
      const yPointUuid = sample.info.yPointUuid;

      if (!equipmentUuid || !eventId) {
        console.error('启停车样本缺少eventId或设备信息');
        return;
      }

      const markTime = sample.info.trendTime;
      const rev = sample.info.rev;

      if (!markTime || !rev) {
        console.error('启停车样本缺少时间戳或转速信息');
        return;
      }

      const waveNumber = sample.info.waveNumber || this.defaultValues.waveNumber;
      const analysisLines = sample.info.analysisLines || this.defaultValues.analysisLines;

      const equipmentUuid1 = encodeURIComponent(equipmentUuid);
      let pointIdString1 = encodeURIComponent(encodeURIComponent(pointIdString || ''));


      const nowtime = new Date(markTime).getTime();

      const waveSpectrumUrl = `/wave-spectrum/${equipmentUuid1}/${pointIdString1}/${nowtime}/${waveNumber}/${analysisLines}/info?eventId=${eventId}&rev=${rev}`;

      let axisUrl;
      if (xPointUuid && yPointUuid) {
        axisUrl = `/axis/${equipmentUuid1}/${encodeURIComponent(xPointUuid)}/${encodeURIComponent(yPointUuid)}/${eventId}/${rev}/hisSudAxisData`;
      } else {
        axisUrl = `/axis/${equipmentUuid1}/${eventId}/${rev}/hisSudAxisData`;
      }

      let waveSpectrumSuccess = false;
      let axisSuccess = false;

      this.$axios.get(waveSpectrumUrl).then(waveSpectrumRes => {
        if (waveSpectrumRes.data.code === 200) {
          const waveData = waveSpectrumRes.data.data.waveValue;
          const spectrumData = waveSpectrumRes.data.data.spectrumValue;

          if (waveData && spectrumData) {
            this.sampleWaveData = waveData;
            this.sampleSpectrumData = spectrumData;

            if (this.$refs.waveg && typeof this.$refs.waveg.drawSampleWaveData === 'function') {
              this.$nextTick(() => {
                setTimeout(() => {
                  this.$refs.waveg.drawSampleWaveData(waveData, sample.name);
                }, 100);
              });
            }

            if (drawSpectrum && this.$refs.spectrumg && typeof this.$refs.spectrumg.drawSampleSpectrumgData === 'function') {
              this.$nextTick(() => {
                setTimeout(() => {
                  this.$refs.spectrumg.drawSampleSpectrumgData(spectrumData, sample.name);
                }, 100);
              });
            }
            waveSpectrumSuccess = true;
          } else {
            console.error('响应数据缺少波形或频谱数据');
          }
        } else {
          console.error('启停车样本 - 波形频谱接口返回错误:', waveSpectrumRes.data);
        }
      }).catch(error => {
        console.error('启停车样本 - 波形频谱接口调用失败:', error);
      });

      this.$axios.get(axisUrl).then(axisRes => {
        if (axisRes.data.code === 200) {
          const axisData = axisRes.data.data;

          if (axisData) {
            let axisInfo = [];
            if (axisData.horDataY && axisData.verDataY) {
              for (let item in axisData.horDataY) {
                axisInfo.push([axisData.horDataY[item], axisData.verDataY[item]]);
              }
            }

            const samplesData = [{
              name: sample.name,
              axisInfo: axisInfo,
              waveXList: {
                waveX: axisData.horDataX,
                waveY: axisData.horDataY
              },
              waveYList: {
                waveX: axisData.verDataX,
                waveY: axisData.verDataY
              },
              status: axisData.status || 1
            }];

            if (this.$refs.scatterOrbit && typeof this.$refs.scatterOrbit.drawMultipleSamples === 'function') {
              this.$nextTick(() => {
                setTimeout(() => {
                  this.$refs.scatterOrbit.drawMultipleSamples(samplesData);
                }, 100);
              });
            }

            if (this.$refs.scatterLeft && typeof this.$refs.scatterLeft.drawMultipleSamples === 'function') {
              this.$nextTick(() => {
                setTimeout(() => {
                  this.$refs.scatterLeft.drawMultipleSamples(samplesData);
                }, 100);
              });
            }

            if (this.$refs.scatterRight && typeof this.$refs.scatterRight.drawMultipleSamples === 'function') {
              this.$nextTick(() => {
                setTimeout(() => {
                  this.$refs.scatterRight.drawMultipleSamples(samplesData);
                }, 100);
              });
            }
            axisSuccess = true;
          } else {
            console.warn('启停车样本 - 轴心轨迹数据为空');
          }
        } else {
          console.error('启停车样本 - 轴心轨迹接口返回错误:', axisRes.data);
        }
      }).catch(error => {
        console.error('启停车样本 - 轴心轨迹接口调用失败:', error);
      });
    },

    /**
     * 处理启停车多样本选择事件
     * 用于在非启停车页面绘制启停车样本数据
     */
    handleStartStopSamplesSelected(samples) {

      if (!samples || samples.length === 0) {
        console.error('样本数据无效');
        return;
      }

      samples.forEach(s => {
        if (!s) {
          console.error('样本数据无效');
          return;
        }
        if (!s.info) {
          console.error('样本数据缺少info对象');
          return;
        }
      });

      if (samples.length > this.maxSamples) {
        console.error(`最多只能选择${this.maxSamples}个样本`);
        return;
      }
      const requests = samples.map((sample, index) => {

        const equipmentUuid = sample.equipmentUuid;
        const eventId = sample.info.eventId;
        const pointIdString = sample.info.pointIdString || sample.info.pointname;
        const xPointUuid = sample.info.xPointUuid;
        const yPointUuid = sample.info.yPointUuid;

        if (!equipmentUuid || !eventId) {
          console.error(`样本 ${index + 1} - ${sample.name} 缺少eventId或设备信息`);
          return Promise.reject(new Error(`样本 ${sample.name} 缺少eventId或设备信息`));
        }

        const markTime = sample.info.trendTime || sample.markTime;
        const rev = sample.info.rev;

        if (!markTime || !rev) {
          console.error(`样本 ${index + 1} - ${sample.name} 缺少时间戳或转速信息`);
          return Promise.reject(new Error(`样本 ${sample.name} 缺少时间戳或转速信息`));
        }

        const waveNumber = sample.info.waveNumber || this.defaultValues.waveNumber;
        const analysisLines = sample.info.analysisLines || this.defaultValues.analysisLines;

        const equipmentUuid1 = encodeURIComponent(equipmentUuid);
        let pointIdString1 = encodeURIComponent(encodeURIComponent(pointIdString || ''));


        const nowtime = new Date(markTime).getTime();

        const waveSpectrumUrl = `/wave-spectrum/${equipmentUuid1}/${pointIdString1}/${nowtime}/${waveNumber}/${analysisLines}/info?eventId=${eventId}&rev=${rev}`;

        let axisUrl;
        if (xPointUuid && yPointUuid) {
          axisUrl = `/axis/${equipmentUuid1}/${encodeURIComponent(xPointUuid)}/${encodeURIComponent(yPointUuid)}/${eventId}/${rev}/hisSudAxisData`;
        } else {
          axisUrl = `/axis/${equipmentUuid1}/${eventId}/${rev}/hisSudAxisData`;
        }

        return Promise.all([
          this.$axios.get(waveSpectrumUrl),
          this.$axios.get(axisUrl)
        ]).then(([waveSpectrumRes, axisRes]) => {

          if (waveSpectrumRes.data.code !== 200) {
            console.error(`样本 ${index + 1} - ${sample.name} 波形频谱接口返回错误:`, waveSpectrumRes.data);
            return null;
          }

          if (axisRes.data.code !== 200) {
            console.error(`样本 ${index + 1} - ${sample.name} 轴心轨迹接口返回错误:`, axisRes.data);
            return null;
          }

          const waveData = waveSpectrumRes.data.data.waveValue;
          const spectrumData = waveSpectrumRes.data.data.spectrumData;
          const axisData = axisRes.data.data;

          if (!waveData || !axisData) {
            console.error(`样本 ${index + 1} - ${sample.name} 数据不完整`);
            return null;
          }

          return {
            sampleName: sample.name,
            waveData: waveData,
            spectrumData: spectrumData,
            axisData: axisData
          };
        }).catch(error => {
          console.error(`样本 ${index + 1} - ${sample.name} 接口调用失败:`, error);
          return null;
        });
      });

      Promise.all(requests).then(results => {
        const validResults = results.filter(r => r !== null);

        if (validResults.length === 0) {
          console.error('所有样本数据获取失败');
          return;
        }

        this.drawMultipleStartStopSamples(validResults);
      }).catch(error => {
        console.error('启停车多样本逻辑失败:', error);
      });
    },

    /**
     * 处理活塞杆沉降/偏摆样本选择事件
     * 用于绘制活塞杆沉降/偏摆的波形和频谱样本数据
     */
    handlePistonRodSettlementSampleSelected(sample) {

      if (!sample) {
        console.error('样本数据无效');
        return;
      }
      if (!sample.info) {
        console.error('样本数据缺少info对象');
        return;
      }


      const equipmentUuid = this.nowpointid || sample.equipmentUuid;

      let pointIdString;

      if (this.tongdao === 0) {
        pointIdString = this.vibPointIdString;
      } else if (this.tongdao === 1) {
        pointIdString = this.dynPointIdString;
      } else if (this.tongdao === 2) {
        pointIdString = this.procPointIdString;
      } else {
        pointIdString = this.pointIdString;
      }


      if (!pointIdString && this.nowtree && this.nowtree.length > 0) {
        const firstPoint = this.nowtree[0];

        if (firstPoint && firstPoint.pointId) {
          const parts = firstPoint.pointId.split('/');
          if (parts.length >= 2) {
            const extractedPointIdString = parts.slice(1).join('/');

            pointIdString = extractedPointIdString;
          }
        }
      }


      if (!equipmentUuid || !pointIdString) {
        console.error('样本数据缺少设备或测点信息');
        return;
      }

      const markTime = sample.info.trendTime || sample.markTime;

      if (!markTime) {
        console.error('样本数据缺少时间戳');
        return;
      }

      const equipmentUuid1 = encodeURIComponent(equipmentUuid);
      let pointIdString1 = encodeURIComponent(encodeURIComponent(pointIdString));


      const nowtime = new Date(markTime).getTime();
      const url = `/rodsink/${equipmentUuid1}/${pointIdString1}/${nowtime}/info`;


      this.$axios.get(url).then(res => {

        if (res.data.code === 200) {
          const data = res.data.data;

          if (data && data.waveValue && data.spectrumValue) {
            const waveData = data.waveValue;
            const spectrumData = data.spectrumValue;
            const startIndex = data.startIndex || 0;
            const endIndex = data.endIndex || waveData.waveX.length;

            const convertedWaveX = generateXAxisData(waveData.waveY.length, startIndex, endIndex);

            const processedWaveData = {
              ...waveData,
              waveX: convertedWaveX
            };

            this.sampleWaveData = processedWaveData;
            this.sampleSpectrumData = spectrumData;

            if (this.$refs.waveg && typeof this.$refs.waveg.drawSampleWaveData === 'function') {
              this.$refs.waveg.drawSampleWaveData(processedWaveData, sample.name);
            }

            if (this.$refs.spectrumg && typeof this.$refs.spectrumg.drawSampleSpectrumgData === 'function') {
              this.$refs.spectrumg.drawSampleSpectrumgData(spectrumData, sample.name);
            }

            const waveg = this.$refs.waveg;
            if (waveg && waveg.mychart) {
              const currentOption = waveg.mychart.getOption();
              const hasOriginalSeries = currentOption.series && currentOption.series.some(series => !series.isSampleSeries);
              if (hasOriginalSeries) {
                let myYmax, myYmin;
                for (let i = 0; i < currentOption.series.length; i++) {
                  const series = currentOption.series[i];
                  if (!series.isSampleSeries) {
                    for (let j = 0; j < series.data.length; j++) {
                      const yValue = series.data[j][1];
                      if (j === 0) {
                        myYmax = yValue;
                        myYmin = yValue;
                      } else {
                        myYmax = Math.max(myYmax, yValue);
                        myYmin = Math.min(myYmin, yValue);
                      }
                    }
                    break;
                  }
                }

                let roundedYMax = waveg.roundUp(myYmin, myYmax);
                let roundedYMin = waveg.roundDown(myYmin, myYmax);

                if (processedWaveData && processedWaveData.waveY) {
                  const sampleYData = processedWaveData.waveY;
                  const sampleMax = Math.max(...sampleYData.filter(val => val !== undefined && val !== null));

                  if (sampleMax > myYmax) {
                    roundedYMax = waveg.roundUp(roundedYMin, sampleMax);
                    if (roundedYMin < 0) {
                      const absMax = Math.max(Math.abs(roundedYMin), Math.abs(roundedYMax));
                      roundedYMax = absMax;
                      roundedYMin = -absMax;
                    }
                  }
                }

                if (roundedYMin < 0 && roundedYMax > 0) {
                  const absMax = Math.max(Math.abs(roundedYMin), Math.abs(roundedYMax));
                  roundedYMax = absMax;
                  roundedYMin = -absMax;
                }

                waveg.mychart.setOption({
                  yAxis: {
                    min: roundedYMin,
                    max: roundedYMax
                  }
                });
              }
            }

            const spectrumg = this.$refs.spectrumg;
            if (spectrumg && spectrumg.mychart) {
              const currentOption = spectrumg.mychart.getOption();
              const hasOriginalSeries = currentOption.series && currentOption.series.some(series => !series.isSampleSeries);
              if (hasOriginalSeries) {
                let myYmax, myYmin;
                for (let i = 0; i < currentOption.series.length; i++) {
                  const series = currentOption.series[i];
                  if (!series.isSampleSeries) {
                    for (let j = 0; j < series.data.length; j++) {
                      const yValue = series.data[j][1];
                      if (j === 0) {
                        myYmax = yValue;
                        myYmin = yValue;
                      } else {
                        myYmax = Math.max(myYmax, yValue);
                        myYmin = Math.min(myYmin, yValue);
                      }
                    }
                    break;
                  }
                }

                let roundedYMax = spectrumg.roundUp(myYmin, myYmax);
                let roundedYMin = spectrumg.roundDown(myYmin, myYmax);

                if (spectrumData && spectrumData.spectrumY) {
                  const sampleYData = spectrumData.spectrumY;
                  const sampleMax = Math.max(...sampleYData.filter(val => val !== undefined && val !== null));

                  if (sampleMax > myYmax) {
                    roundedYMax = spectrumg.roundUp(roundedYMin, sampleMax);
                    if (roundedYMin < 0) {
                      const absMax = Math.max(Math.abs(roundedYMin), Math.abs(roundedYMax));
                      roundedYMax = absMax;
                      roundedYMin = -absMax;
                    }
                  }
                }

                if (roundedYMin < 0 && roundedYMax > 0) {
                  const absMax = Math.max(Math.abs(roundedYMin), Math.abs(roundedYMax));
                  roundedYMax = absMax;
                  roundedYMin = -absMax;
                }

                spectrumg.mychart.setOption({
                  yAxis: {
                    min: roundedYMin,
                    max: roundedYMax
                  }
                });
              }
            }
          } else {
            console.log('数据检查失败:', {
              hasData: !!data,
              hasWaveValue: !!(data && data.waveValue),
              hasSpectrumValue: !!(data && data.spectrumValue)
            });
            console.error('响应数据缺少波形或频谱数据');
          }
        } else {
          console.error(`获取样本数据失败：${res.data.message}`);
        }
      }).catch(error => {
        console.log('请求错误:', error);
        console.error('获取样本数据失败：网络请求错误');
      });
    },

    drawMultipleStartStopSamples(results) {
      const samplesData = results.map((result, index) => {

        const axisData = result.axisData;
        let axisInfo = [];
        if (axisData.horDataY && axisData.verDataY) {
          for (let item in axisData.horDataY) {
            axisInfo.push([axisData.horDataY[item], axisData.verDataY[item]]);
          }
        }

        return {
          name: result.sampleName,
          axisInfo: axisInfo,
          waveXList: {
            waveX: axisData.horDataX,
            waveY: axisData.horDataY
          },
          waveYList: {
            waveX: axisData.verDataX,
            waveY: axisData.verDataY
          },
          status: axisData.status || 1,
          waveData: result.waveData,
          spectrumData: result.spectrumData
        };
      });

      if (this.$refs.scatterOrbit && typeof this.$refs.scatterOrbit.drawMultipleSamples === 'function') {
        this.$nextTick(() => {
          setTimeout(() => {
            this.$refs.scatterOrbit.drawMultipleSamples(samplesData);
          }, 100);
        });
      }

      if (this.$refs.scatterLeft && typeof this.$refs.scatterLeft.drawMultipleSamples === 'function') {
        this.$nextTick(() => {
          setTimeout(() => {
            this.$refs.scatterLeft.drawMultipleSamples(samplesData);
          }, 100);
        });
      }

      if (this.$refs.scatterRight && typeof this.$refs.scatterRight.drawMultipleSamples === 'function') {
        this.$nextTick(() => {
          setTimeout(() => {
            this.$refs.scatterRight.drawMultipleSamples(samplesData);
          }, 100);
        });
      }

      const wave1Data = samplesData.map((s, index) => {
        return {
          name: s.name,
          waveX: s.waveData.waveX || [],
          waveY: s.waveData.waveY || []
        };
      });
      const wave2Data = samplesData.map((s, index) => {
        return {
          name: s.name,
          waveX: s.waveData.waveX || [],
          waveY: s.waveData.waveY || []
        };
      });


      if (this.$refs.wav1 && typeof this.$refs.wav1.drawMultipleSamples === 'function') {
        this.$nextTick(() => {
          setTimeout(() => {
            this.$refs.wav1.drawMultipleSamples(wave1Data);
          }, 100);
        });
      }

      if (this.$refs.wav2 && typeof this.$refs.wav2.drawMultipleSamples === 'function') {
        this.$nextTick(() => {
          setTimeout(() => {
            this.$refs.wav2.drawMultipleSamples(wave2Data);
          }, 100);
        });
      }

      if (this.$refs.spectrumg) {
        if (this.$refs.spectrumg.$options.name === 'SpectrumGraph') {
          if (typeof this.$refs.spectrumg.drawSampleSpectrumgData === 'function') {
            this.$nextTick(() => {
              setTimeout(() => {
                if (results[0] && results[0].spectrumData) {
                  this.$refs.spectrumg.drawSampleSpectrumgData(results[0].spectrumData, results[0].sampleName);
                }
              }, 100);
            });
          }
        } else if (this.$refs.spectrumg.$options.name === 'Cepstrum') {
        }
      }

      this.samplesData = samplesData;
    },


    /**
     * 绘制多个轴心轨迹样本
     */
    drawMultipleOrbitSamples(samplesData) {
      if (this.$refs.scatterLeft) {
        this.$refs.scatterLeft.drawMultipleSamples(samplesData);
      }

      if (this.$refs.scatterRight) {
        this.$refs.scatterRight.drawMultipleSamples(samplesData);
      }

      if (this.$refs.scatterOrbit) {
        this.$refs.scatterOrbit.drawMultipleSamples(samplesData);
      }

      if (this.$refs.wav1) {
        const wave1Data = samplesData.map(s => {

          if (s.waveData && s.waveData.waveX) {
            return {
              name: s.name,
              waveX: s.waveData.waveX,
              waveY: s.waveData.waveY
            };
          } else if (s.waveXList && s.waveXList.waveX !== undefined) {
            return {
              name: s.name,
              waveX: s.waveXList.waveX,
              waveY: s.waveXList.waveY
            };
          } else {
            return {
              name: s.name,
              waveX: s.waveXList,
              waveY: s.waveYList
            };
          }
        });
        this.$refs.wav1.drawMultipleSamples(wave1Data);
      }

      if (this.$refs.wav2) {
        const wave2Data = samplesData.map(s => {

          if (s.waveData && s.waveData.waveX) {
            return {
              name: s.name,
              waveX: s.waveData.waveX,
              waveY: s.waveData.waveY
            };
          } else if (s.waveYList && s.waveYList.waveX !== undefined) {
            return {
              name: s.name,
              waveX: s.waveYList.waveX,
              waveY: s.waveYList.waveY
            };
          } else {
            return {
              name: s.name,
              waveX: s.waveXList,
              waveY: s.waveYList
            };
          }
        });
        this.$refs.wav2.drawMultipleSamples(wave2Data);
      }

      this.samplesData = samplesData;
    },

    /**
     * 刷新轴心轨迹样本数据（公共方法）
     * 当测点切换时，使用新的测点重新获取样本数据并绘制
     */
    refreshOrbitSamples() {
      if (!this.selectedSamples || this.selectedSamples.length === 0) {
        return;
      }

      const hasStartStopSample = this.selectedSamples.some(sample => sample.info && sample.info.eventId);

      if (hasStartStopSample) {
        if (this.samplesData && this.samplesData.length > 0) {
          this.drawMultipleOrbitSamples(this.samplesData);
        }
        return;
      }

      const requests = this.selectedSamples.map((sample, index) => {
        let xPointUuid, yPointUuid, markTime, equipmentUuid, filterType;

        markTime = sample.info.markTime || sample.info.trendTime || sample.markTime;
        equipmentUuid = sample.equipmentUuid;
        filterType = this.filterType;

        if (this.nowtree && this.nowtree[1]) {
          xPointUuid = this.nowtree[1].xid;
          yPointUuid = this.nowtree[1].yid;
        } else {
          console.error(`样本 ${index + 1} - 无法通过当前数据找到对应的测点对`);
          return Promise.reject(new Error(`样本 ${sample.name} 无法找到当前数据的测点对`));
        }

        if (!xPointUuid || !yPointUuid || !markTime || !equipmentUuid) {
          console.error(`样本 ${index + 1} - 缺少必要参数:`, {xPointUuid, yPointUuid, markTime, equipmentUuid});
          return Promise.reject(new Error(`样本 ${sample.name} 缺少必要参数`));
        }

        const url = `/axis/${encodeURIComponent(equipmentUuid)}/${encodeURIComponent(xPointUuid)}/${encodeURIComponent(yPointUuid)}/${markTime}/${filterType}/hisAxis`;

        return this.$axios.get(url);
      });

      if (requests.length === 0) {
        console.error('没有有效的样本数据');
        return;
      }

      Promise.all(requests).then(responses => {
        const samplesData = responses.map((res, index) => {
          if (res.data.code === 200) {
            return {
              name: this.selectedSamples[index].name,
              axisInfo: res.data.data.axisInfo,
              waveXList: res.data.data.waveXList,
              waveYList: res.data.data.waveYList,
              status: res.data.data.status
            };
          }
          return null;
        }).filter(item => item !== null);

        this.samplesData = samplesData;
        this.drawMultipleOrbitSamples(samplesData);
      }).catch(error => {
        console.error('刷新样本数据失败:', error);
      });
    },

  },

}

export default EventComparisonMixin



// WEBPACK FOOTER //
// ./src/mixins/EventComparisonMixin.js